import type {
  AlertFilter,
  AlertKind,
  AnalyticsApplicationsQuery,
  AnalyticsTopUsersQuery,
  AnalyticsWindowQuery,
  AppMetadataFilter,
  AuditFilter,
  FailureQuery,
  ModelUsageFilter,
  SecurityEventFilter,
  MarkupPlanFilter,
  PackageFilter,
  PriceProposalFilter,
  PriceVersionFilter,
  ReservationFilter,
  TopUpFilter,
  UsageDriftQuery,
  UserFilter,
  WalletFilter,
} from "./admin-client";

/**
 * The admin portal's query keys.
 *
 * Every key starts with "admin" so signing out can drop the whole realm with
 * one prefix, and so a key from here can never collide with a customer-realm
 * key if the two ever share a cache in a test.
 *
 * The nesting is chosen for invalidation, not for tidiness: a user's detail
 * lives UNDER the users prefix so revoking a device — which changes the row
 * in the list as well as the page in front of the operator — can invalidate
 * `users` once and refresh both. A screen that invented its own key string
 * would invalidate nothing, and the stale row it left behind would say
 * "active" about a device that is not.
 */
export const adminKeys = {
  all: ["admin"] as const,
  session: ["admin", "session"] as const,
  reasonCodes: ["admin", "reason-codes"] as const,
  applications: ["admin", "applications"] as const,
  /**
   * The tenant's own seats — its operators and the invitations it has
   * outstanding — under ONE prefix, because they are two halves of one answer:
   * inviting somebody adds a pending row, and their accepting moves them from
   * the second list to the first. A mutation invalidates `team` and both
   * refresh; two sibling prefixes would leave an accepted invitation still
   * drawn as pending next to the member it became.
   */
  team: ["admin", "team"] as const,
  teamMembers: ["admin", "team", "members"] as const,
  teamInvites: ["admin", "team", "invites"] as const,
  /** The tenant's API keys. Keyed by the tenant platform staff named, or ""
   *  for an operator's own, so two tenants' lists never share a cache row. */
  tenantAPIKeys: (tenantID: string) => ["admin", "tenant-api-keys", tenantID] as const,
  tenantAPIKeysAll: ["admin", "tenant-api-keys"] as const,
  application: (applicationID: string) => ["admin", "applications", applicationID] as const,
  users: ["admin", "users"] as const,
  // `developer ?? null` rather than `?? false`: "not filtered" and "only the
  // non-developers" are two different questions and must not share one cache
  // entry, or the unfiltered list shows whichever answer landed last.
  userList: (filter: UserFilter) =>
    [
      "admin",
      "users",
      "list",
      filter.q ?? "",
      filter.status ?? "",
      filter.developer ?? null,
      filter.limit ?? null,
    ] as const,
  user: (userID: string) => ["admin", "users", "detail", userID] as const,
  /**
   * A user's subscriptions, under the USER prefix and not the packages one.
   *
   * It is read on the user page and it is about one account, so suspending or
   * revoking — which already invalidate `users` — refresh it with everything
   * else on that page. It also sits under `packages` conceptually; migrating
   * subscribers invalidates that prefix and would leave this stale, which is
   * why `useMigratePackageSubscribers` invalidates `users` too.
   */
  userSubscriptions: (userID: string) =>
    ["admin", "users", "detail", userID, "subscriptions"] as const,

  /**
   * One account's authentication and device trail, keyed by the WHOLE filter.
   *
   * Every member is in the key, `limit` included, because "show me more" is a
   * different question with a different answer and the two must not share a
   * cache entry — a 25-row page left under the key a 50-row page then reads
   * would show an operator a page they did not ask for and give them no way
   * to tell. `?? ""` / `?? null` keep "not filtered" apart from a filter that
   * happens to be empty, exactly as userList does.
   *
   * Under the USER prefix, so revoking a device or a session — which is an
   * act this trail records — refreshes it with the rest of the page.
   */
  userSecurityEvents: (userID: string, filter: SecurityEventFilter) =>
    [
      "admin",
      "users",
      "detail",
      userID,
      "security-events",
      filter.limit ?? null,
      filter.outcome ?? "",
      filter.event_type ?? "",
      filter.since ?? "",
      filter.until ?? "",
    ] as const,

  /**
   * What one account's installations have reported about themselves, keyed by
   * the WHOLE filter.
   *
   * Under the USER prefix, so it refreshes with the rest of the page. Every
   * member is in the key for userSecurityEvents' reason — "show me more" and
   * "only the plugins" are different questions with different answers and must
   * not share a cache entry.
   */
  userAppMetadata: (userID: string, filter: AppMetadataFilter) =>
    [
      "admin",
      "users",
      "detail",
      userID,
      "app-metadata",
      filter.limit ?? null,
      filter.source ?? "",
    ] as const,

  /**
   * The same table read one INSTALLATION at a time.
   *
   * `full` is in the key and must be: the elided page and the whole one are
   * different documents at the same URL, and a screen that flipped the flag
   * while react-query served the cached elided page would show an operator
   * `payload_truncated: true` on a request that asked for everything.
   *
   * Under a `devices` prefix of its own rather than under the user page it is
   * reached from, because a report is a fact about the MACHINE: it survives
   * the account, and revoking a device does not change what that device once
   * told us.
   */
  deviceAppMetadata: (deviceID: string, filter: AppMetadataFilter) =>
    [
      "admin",
      "devices",
      "detail",
      deviceID,
      "app-metadata",
      filter.limit ?? null,
      filter.source ?? "",
      filter.full ?? false,
    ] as const,

  /**
   * Providers, models, routing and pricing.
   *
   * `providers` is both the list's key and the prefix every account detail
   * hangs off, so a rotation — which changes the credential counts on the
   * list as well as the table the operator is looking at — can refresh both
   * by invalidating one prefix, while `provider(id)` still names the single
   * page when that is all that moved.
   */
  providers: ["admin", "providers"] as const,
  provider: (accountID: string) => ["admin", "providers", "detail", accountID] as const,
  /**
   * The provider templates, and the filtered list under the same prefix.
   *
   * OUTSIDE the `providers` prefix: an account is a tenant's own row and a
   * template is the platform's, so adding an account must not re-fetch the
   * catalogue and retiring a template must not claim to have changed anybody's
   * pool. The status is in the key because "everything" and "only published"
   * are two different answers — and which one a caller even gets back depends
   * on their scope, so they cannot share an entry.
   */
  /**
   * The operator tier price list, and the roster of who is on which tier.
   *
   * SIBLINGS RATHER THAN NESTED, because they answer to different writes: a
   * tier edit changes the price list and what the NEXT period of that tier
   * opens with, and changes nobody's current consumption — so invalidating the
   * catalogue must not claim the roster's usage numbers moved, and a roster
   * refetch must not claim the price list did.
   */
  tenantTiers: ["admin", "tenant-tiers"] as const,
  tenantRoster: ["admin", "tenant-roster"] as const,
  /** One roster row. Under the roster prefix, so a tier edit that invalidates
   *  the roster refreshes an open company page with it. */
  tenant: (tenantID: string) => ["admin", "tenant-roster", tenantID] as const,
  /**
   * The CALLER's own bill.
   *
   * A THIRD sibling, not a branch of either above. It is the only one of the
   * three a tenant operator can read at all, and it answers to a different
   * write again: buying a tier moves this company's own tier and allowance and
   * says nothing about the platform's price list — while a tier edit next door
   * changes what the NEXT period opens with here and moves nothing today.
   */
  tenantBilling: ["admin", "tenant-billing"] as const,
  /** Under tenantBilling, so a cancel or tier change that invalidates the
   *  bill also refreshes the notices drawn beside it. */
  tenantNotifications: ["admin", "tenant-billing", "notifications"] as const,
  tenantNotificationsFor: (unread: boolean) =>
    ["admin", "tenant-billing", "notifications", unread ? "unread" : "all"] as const,
  providerTemplates: ["admin", "provider-templates"] as const,
  providerTemplatesFor: (status: string) =>
    ["admin", "provider-templates", "status", status] as const,
  models: ["admin", "models"] as const,
  /**
   * The catalogue narrowed to one application.
   *
   * UNDER the `models` prefix on purpose, unlike the usage report below: this
   * is the same rows the unfiltered list returns, so every write that changes
   * the catalogue changes this too and invalidating `models` has to reach it.
   * The application id is in the key because "every model" and "this
   * application's models" are two different answers that must not share an
   * entry.
   */
  modelsFor: (applicationID: string) => ["admin", "models", "application", applicationID] as const,
  /**
   * A vendor's own catalogue, keyed by the account it was read through.
   *
   * OUTSIDE the `models` prefix, unlike modelsFor: this is the provider's
   * answer, not ours, and nothing this console writes can change it. Hanging it
   * under `models` would make every deprecation re-ask a third party.
   */
  availableModels: (accountID: string) => ["admin", "available-models", accountID] as const,
  /**
   * The usage report, deliberately NOT under the `models` prefix.
   *
   * Everything else here is nested for invalidation, and that rule is what
   * keeps this one out: the numbers come from a daily rollup the worker writes
   * once a night, so no write this console can make moves them. Hanging it
   * under `models` would make every deprecation, every capability correction
   * and every reinstate re-fetch a 500-row aggregate that cannot have changed.
   *
   * The whole filter is in the key. A 30-day report and a 365-day one are
   * different answers to different questions and must not share a cache entry
   * — the window is what the column header is labelled with.
   */
  modelUsage: (filter: ModelUsageFilter) =>
    ["admin", "model-usage", filter.window_days ?? null, filter.alias ?? ""] as const,
  routingProfiles: ["admin", "routing-profiles"] as const,
  routingProfile: (profileID: string) =>
    ["admin", "routing-profiles", "detail", profileID] as const,
  /** One prefix over both pricing lists: publishing a price version or a
   *  markup plan closes the previous one, so whichever list is on screen is
   *  wrong afterwards. */
  pricing: ["admin", "pricing"] as const,
  priceVersions: (filter: PriceVersionFilter) =>
    [
      "admin",
      "pricing",
      "price-versions",
      filter.provider ?? "",
      filter.model ?? "",
      filter.limit ?? null,
    ] as const,
  markupPlans: (filter: MarkupPlanFilter) =>
    ["admin", "pricing", "markup-plans", filter.application_id ?? "", filter.limit ?? null] as const,
  /** Under the same `pricing` prefix as the versions, and deliberately so:
   *  publishing a proposal appends a price version AND moves the proposal out
   *  of the queue, so one invalidation has to reach both lists. */
  priceProposals: (filter: PriceProposalFilter) =>
    [
      "admin",
      "pricing",
      "price-proposals",
      filter.status ?? "",
      filter.provider ?? "",
      filter.flagged ?? false,
    ] as const,

  /**
   * Packages.
   *
   * Their own prefix rather than a branch of `pricing`, because the two
   * invalidate in opposite directions: publishing a provider rate says
   * nothing about what a package costs, and re-pricing a package says nothing
   * about what a model costs. `packages` is the prefix over the list AND
   * every package page, so a re-price — which changes the row in the list as
   * well as the page the operator is on — refreshes both with one
   * invalidation.
   */
  packages: ["admin", "packages"] as const,
  packageList: (filter: PackageFilter) =>
    [
      "admin",
      "packages",
      "list",
      filter.application_id ?? "",
      filter.status ?? "",
      filter.limit ?? null,
      // In the key, because "my packages" and "my packages plus the
      // platform's" are two different answers and must not share an entry.
      filter.include_inherited ?? false,
    ] as const,
  package: (packageID: string) => ["admin", "packages", "detail", packageID] as const,

  /**
   * Money.
   *
   * `wallets` is the prefix over the list, every wallet page and every
   * wallet's ledger, so anything that moves a balance refreshes all three
   * with one invalidation. The reservation and top-up lists sit OUTSIDE it
   * because they are read across every wallet at once — a page about the
   * backlog, not about one customer.
   */
  wallets: ["admin", "wallets"] as const,
  walletList: (filter: WalletFilter) =>
    [
      "admin",
      "wallets",
      "list",
      filter.application_id ?? "",
      filter.status ?? "",
      filter.q ?? "",
      filter.limit ?? null,
    ] as const,
  wallet: (walletID: string) => ["admin", "wallets", "detail", walletID] as const,
  walletLedger: (walletID: string, limit?: number) =>
    ["admin", "wallets", "detail", walletID, "ledger", limit ?? null] as const,
  reservations: (filter: ReservationFilter) =>
    [
      "admin",
      "reservations",
      filter.wallet_id ?? "",
      filter.application_id ?? "",
      filter.status ?? "",
      filter.stale ?? false,
      filter.limit ?? null,
    ] as const,
  topUps: (filter: TopUpFilter) =>
    [
      "admin",
      "top-ups",
      filter.wallet_id ?? "",
      filter.application_id ?? "",
      filter.status ?? "",
      filter.unsettled ?? false,
      filter.limit ?? null,
    ] as const,
  settlementFailures: (limit?: number) => ["admin", "settlement-failures", limit ?? null] as const,

  /**
   * Analytics, on its own prefix and deliberately NOT under anything this
   * console can write to — the same reasoning `modelUsage` above spells out.
   * Every figure comes from the worker's nightly rollups, so no suspension,
   * no price publication and no package migration can move one, and hanging
   * these under `users` or `applications` would make every write in the
   * console re-fetch a dashboard that cannot have changed.
   *
   * The whole WINDOW is in the key. A 7-day answer and a 90-day answer are
   * answers to different questions, and the screen labels its tiles from the
   * window the response states — serving one window's numbers under another's
   * heading is the one failure a dashboard must not have.
   */
  analytics: ["admin", "analytics"] as const,
  analyticsOverview: (query: AnalyticsWindowQuery) =>
    [
      "admin",
      "analytics",
      "overview",
      query.since ?? "",
      query.until ?? "",
      query.days ?? null,
    ] as const,

  /**
   * The leaderboard. Every field of the query is in the key, the ordering
   * included: this route ranks and then CUTS, so "the top ten by tokens" and
   * "the top ten by revenue" are not two orderings of one answer — they are
   * two different sets of ten accounts, and serving one under the other's
   * heading would name the wrong people.
   */
  analyticsUsers: (query: AnalyticsTopUsersQuery) =>
    [
      "admin",
      "analytics",
      "users",
      query.since ?? "",
      query.until ?? "",
      query.days ?? null,
      query.application ?? "",
      query.sort ?? "",
      query.order ?? "",
      query.limit ?? null,
    ] as const,

  /**
   * One account's drill-down. The user id is in the key ahead of the window,
   * so `queryClient.removeQueries` over one account's prefix cannot reach
   * another's — and, more to the point, so a cache entry can never be shared
   * between two accounts asked about over the same days.
   */
  analyticsUserDetail: (userID: string, query: AnalyticsWindowQuery) =>
    [
      "admin",
      "analytics",
      "user",
      userID,
      query.since ?? "",
      query.until ?? "",
      query.days ?? null,
    ] as const,

  /**
   * One application's window, for the card on that application's page — and
   * for the whole catalogue when no slug is named.
   *
   * The SLUG is in the key, not the application id: it is what the route is
   * asked with, so keying on anything else would let two asks that differ on
   * the wire share one cache entry. The empty string is "every application",
   * which is a different answer from any one of them rather than a superset a
   * client should slice — the route ranks the catalogue and each row carries
   * its own series.
   */
  analyticsApplications: (query: AnalyticsApplicationsQuery) =>
    [
      "admin",
      "analytics",
      "applications",
      query.since ?? "",
      query.until ?? "",
      query.days ?? null,
      query.application ?? "",
    ] as const,

  /**
   * The money picture, on the same window key as the overview and
   * deliberately NOT sharing its entry.
   *
   * Two documents over one span of days: the overview answers in ONE
   * reporting currency and this one answers per currency, so neither is a
   * slice of the other and a shared entry would serve whichever arrived
   * first. There is nothing else in the query — the route takes no
   * `application`, no `currency` and no `limit` — so the window IS the key.
   */
  analyticsRevenue: (query: AnalyticsWindowQuery) =>
    [
      "admin",
      "analytics",
      "revenue",
      query.since ?? "",
      query.until ?? "",
      query.days ?? null,
    ] as const,

  /**
   * The reconciliation verdicts, under the `analytics` prefix because that is
   * the grant and the screen they belong to.
   *
   * EVERY filter field is in the key, including `drifted`. "The ones that
   * drifted" and "every bucket we compared" are different answers, and serving
   * the narrowed one for the wide one hides exactly the `not_reported` rows
   * that say a provider has gone quiet. `drifted` is spelled as the server's
   * resolved default rather than left empty, so "not asked" does not become a
   * third value of a two-valued filter.
   *
   * Nothing in this console can invalidate it: a verdict changes when the
   * WORKER re-runs a day, never because a screen was opened.
   */
  analyticsDrift: (query: UsageDriftQuery) =>
    [
      "admin",
      "analytics",
      "drift",
      query.from ?? "",
      query.to ?? "",
      query.provider ?? "",
      query.drifted ?? false,
      query.limit ?? null,
    ] as const,

  /**
   * The system failure log.
   *
   * `failures` is the prefix everything hangs off, and it is what an
   * acknowledgement invalidates: clearing a row changes the table the operator
   * is looking at, the nav badge counting the open set, and the summary the
   * charts are drawn from — three entries, one prefix.
   */
  failures: ["admin", "failures"] as const,

  /**
   * One filtered page of faults.
   *
   * EVERY filter field is in the key, on the audit trail's rule: "the worker's
   * failures" and "every failure" are different answers, and serving one for
   * the other on this screen is the console reporting a service as healthy.
   *
   * `limit` is deliberately absent — the screen never sends one, so a key
   * carrying it would have a constant member. The nav badge asks the UNFILTERED
   * question, which is this key with every field empty, so it shares its entry
   * with the screen whenever the screen is unfiltered: one read, two readers.
   */
  failureList: (query: FailureQuery) =>
    [
      "admin",
      "failures",
      "list",
      query.service ?? "",
      query.kind ?? "",
      query.severity ?? "",
      query.since ?? "",
      query.until ?? "",
      query.user ?? "",
      query.device ?? "",
      query.application ?? "",
      // The server's default, spelled out rather than left empty: the resolved
      // value IS "false", and an empty member would make "not asked" a fourth
      // state of a three-valued filter.
      query.acknowledged ?? "false",
    ] as const,

  /**
   * The same filtered set counted four ways — what the charts read.
   *
   * The SAME members as `failureList`, in the same order, because the two
   * documents are two views of one set and a key that carried a filter the
   * other did not would draw a chart of one question over a table of another.
   * It is a sibling entry rather than the same one: the summary route resolves
   * a DIFFERENT window (thirty days by default, where the list has none), so
   * the two answers are not interchangeable and must not share a cache slot.
   */
  failureSummary: (query: FailureQuery) =>
    [
      "admin",
      "failures",
      "summary",
      query.service ?? "",
      query.kind ?? "",
      query.severity ?? "",
      query.since ?? "",
      query.until ?? "",
      query.user ?? "",
      query.device ?? "",
      query.application ?? "",
      query.acknowledged ?? "false",
    ] as const,

  /**
   * One fault whole, for an expanded row.
   *
   * Under the same `failures` prefix as the list rather than beside it: the
   * detail is the row, and an acknowledgement that refreshes the table has to
   * refresh the open rows drawn from it or the panel keeps saying "open" about
   * a fault the operator just cleared.
   */
  failure: (failureID: string) => ["admin", "failures", "detail", failureID] as const,

  /**
   * The alerting spine: what has fired, and one crossing whole.
   *
   * `alerts` is the prefix over both, on the `failures` precedent — the list
   * and the open row are two reads of the same crossing, and anything that
   * changed one would have to change the other. Nothing in this console CAN
   * change one: resolving and acknowledging are edges the engine observes, so
   * there is no mutation under this prefix to invalidate it, and the rows go
   * stale because the platform moved rather than because the portal did.
   */
  alerts: ["admin", "alerts"] as const,

  /**
   * One filtered page of crossings.
   *
   * EVERY filter field is in the key, on `failureList`'s rule: "the critical
   * ones" and "all of them" are different answers, and serving one for the
   * other on this screen is the console reporting that nobody is running out
   * of money. `state` is spelled as the server's resolved default rather than
   * left empty, so "not asked" does not become a third value of a two-valued
   * filter.
   */
  alertList: (filter: AlertFilter) =>
    [
      "admin",
      "alerts",
      "list",
      filter.kind ?? "",
      filter.severity ?? "",
      filter.state ?? "open",
      filter.account_id ?? "",
      filter.application_id ?? "",
      filter.limit ?? null,
    ] as const,

  alert: (alertID: string) => ["admin", "alerts", "detail", alertID] as const,

  /**
   * The per-account threshold, on its OWN prefix and deliberately not under
   * `alerts`.
   *
   * They invalidate in opposite directions. Moving the line changes what the
   * NEXT sweep fires and changes no crossing that has already happened — so a
   * threshold edit must not refetch a list of alerts it cannot have altered,
   * and it must certainly not make a screen redraw fired rows as though they
   * had been re-measured against the new figure.
   *
   * The kind is in the key because one account has one threshold PER kind, and
   * serving the runout line under a drift heading would show an operator a
   * number that governs something else.
   */
  alertThresholds: ["admin", "alert-thresholds"] as const,
  alertThreshold: (accountID: string, kind: AlertKind) =>
    ["admin", "alert-thresholds", accountID, kind] as const,

  /** The audit trail. Every filter field is in the key: two operators' rows
   *  and one operator's rows are different answers, and serving one for the
   *  other is a lie about who did something. */
  audit: (filter: AuditFilter) =>
    [
      "admin",
      "audit",
      filter.admin_user_id ?? "",
      filter.resource_type ?? "",
      filter.outcome ?? "",
      filter.action ?? "",
      filter.since ?? "",
      filter.until ?? "",
      filter.limit ?? null,
    ] as const,
};
